import React, { useState, useEffect } from 'react';
import { PostCard } from '../components/post/PostCard';
import { FollowSuggestions } from '../components/profile/FollowSuggestions';
import { posts, suggestedUsers } from '../data/mockData';
import { useAuthStore } from '../store/authStore';
import { getAlgorithmicFeed } from '../lib/discoveryService';
import { realtimeEngagementService } from '../lib/realtimeEngagementService';
import { Post, User } from '../types';

export const HomePage: React.FC = () => {
  const { user } = useAuthStore();
  const [feedPosts, setFeedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedType, setFeedType] = useState<'algorithmic' | 'following'>('algorithmic');

  useEffect(() => {
    loadFeedPosts();
  }, [user, feedType]);

  // Subscribe to global feed updates for real-time engagement
  useEffect(() => {
    if (!user) return;

    console.log('🔄 Setting up global feed engagement updates');

    const unsubscribe = realtimeEngagementService.subscribeToFeedUpdates(
      handleFeedEngagementUpdate
    );

    return () => {
      console.log('🔌 Cleaning up global feed engagement subscription');
      unsubscribe();
    };
  }, [user]);

  const handleFeedEngagementUpdate = (payload: any) => {
    console.log('📊 Handling feed engagement update:', payload);
    
    const { post_id, engagement_type, action, new_count } = payload;
    
    setFeedPosts(prevPosts => 
      prevPosts.map(post => {
        if (post.id === post_id) {
          const updatedPost = { ...post };
          
          if (engagement_type === 'like' && new_count !== undefined) {
            updatedPost.like_count = new_count;
          } else if (engagement_type === 'comment' && new_count !== undefined) {
            updatedPost.comment_count = new_count;
          }
          
          console.log(`✅ Updated ${engagement_type} count for post ${post_id} to ${new_count}`);
          return updatedPost;
        }
        return post;
      })
    );
  };

  const loadFeedPosts = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (feedType === 'algorithmic' && user?.id) {
        // Load algorithmic feed
        const algorithmicPosts = await getAlgorithmicFeed(user.id, 20);
        
        // Combine with mock posts for demo
        const allPosts = [...algorithmicPosts, ...posts];
        
        // Remove duplicates and sort
        const uniquePosts = allPosts.filter((post, index, self) => 
          index === self.findIndex(p => p.id === post.id)
        );
        
        // Sort by creation date (newest first)
        uniquePosts.sort((a, b) => {
          const dateA = new Date(a.created_at || a.timestamp || '').getTime();
          const dateB = new Date(b.created_at || b.timestamp || '').getTime();
          return dateB - dateA;
        });
        
        setFeedPosts(uniquePosts);
      } else {
        // Load following feed (chronological) or fallback to mock data
        setFeedPosts(posts);
      }
    } catch (err) {
      console.error('Error loading feed posts:', err);
      setError('Unable to load posts from database. Showing sample content.');
      // Fallback to mock data
      setFeedPosts(posts);
    } finally {
      setLoading(false);
    }
  };

  const handlePostDeleted = (postId: string) => {
    setFeedPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
  };

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-lg">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50">
      <div className="container mx-auto px-4 max-w-screen-lg">
        <div className="flex flex-col md:flex-row">
          {/* Feed */}
          <div className="w-full md:w-2/3 md:pr-6">
            {/* Feed Type Toggle */}
            <div className="bg-white border border-gray-200 rounded-md p-4 mb-6">
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setFeedType('algorithmic')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    feedType === 'algorithmic'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  For You
                </button>
                <button
                  onClick={() => setFeedType('following')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    feedType === 'following'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Following
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md mb-6 text-sm">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            {/* Real-time Status Indicator */}
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md mb-6 text-sm">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Real-time updates enabled - likes and comments will update automatically
              </div>
            </div>

            {/* Stories */}
            <div className="bg-white border border-gray-200 rounded-md p-4 mb-6">
              <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-hide">
                {suggestedUsers.map((user) => (
                  <div key={user.id} className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full p-0.5 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600">
                      <div className="bg-white p-0.5 rounded-full w-full h-full">
                        <img 
                          src={user.profileImage} 
                          alt={user.username} 
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                    </div>
                    <span className="text-xs mt-1 truncate w-16 text-center">{user.username}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Posts */}
            {feedPosts.length > 0 ? (
              feedPosts.map((post) => (
                <PostCard 
                  key={post.id} 
                  post={post} 
                  onPostDeleted={handlePostDeleted}
                />
              ))
            ) : (
              <div className="bg-white border border-gray-200 rounded-md p-8 text-center">
                <p className="text-gray-500">No posts to show</p>
                <p className="text-sm text-gray-400 mt-1">
                  {feedType === 'following' 
                    ? 'Follow some users to see their posts here!'
                    : 'Check back later for new content!'
                  }
                </p>
              </div>
            )}
          </div>
          
          {/* Sidebar - Suggestions */}
          <div className="hidden md:block md:w-1/3">
            <div className="sticky top-20 space-y-6">
              {/* User Account */}
              <div className="flex items-center">
                <img 
                  src={posts[0].user.profileImage} 
                  alt={posts[0].user.username}
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div className="ml-3">
                  <p className="font-semibold text-sm">{posts[0].user.username}</p>
                  <p className="text-gray-500 text-sm">{posts[0].user.displayName}</p>
                </div>
              </div>
              
              {/* Follow Suggestions */}
              <FollowSuggestions limit={5} />
              
              {/* Footer Links */}
              <div>
                <div className="flex flex-wrap text-xs text-gray-500 mb-4">
                  <a href="#" className="mr-2 mb-1">About</a>
                  <a href="#" className="mr-2 mb-1">Help</a>
                  <a href="#" className="mr-2 mb-1">Press</a>
                  <a href="#" className="mr-2 mb-1">API</a>
                  <a href="#" className="mr-2 mb-1">Jobs</a>
                  <a href="#" className="mr-2 mb-1">Privacy</a>
                  <a href="#" className="mr-2 mb-1">Terms</a>
                  <a href="#" className="mr-2 mb-1">Locations</a>
                </div>
                <p className="text-xs text-gray-500">© 2025 PETSOCIAL</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};